/**
 * PropertyResultCard - Zeilian Property Card (Matching Exact Design)
 * 
 * Layout as per design:
 * - Image carousel on left (max 10 images based on subscription)
 * - Location top-right
 * - Price with weekly rate
 * - Availability status
 * - Property type with bed/bath icons
 * - Distance (km for Sri Lanka, miles for UK)
 * - Short description
 * - Advertiser with verification status
 * - Action bar: Call, Message, Email + Save, Share, Compare
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { 
  Heart, BedDouble, Bath, Phone, MessageCircle, Mail,
  Share2, Plus, ChevronLeft, ChevronRight, Play, Scan,
  MapPin, Sofa, CheckCircle2, Building2, User, Clock, Camera
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// ADVERTISER IDENTITY COMPONENT
// =============================================================================
const AdvertiserIdentity = ({ advertiser, onAdvertiserClick, size = 'default' }) => {
  const defaultAdvertiser = {
    name: 'Private Advertiser',
    type: 'private',
    verification_level: 0
  };
  
  const {
    name = 'Private Advertiser',
    logo,
    type = 'private',
    verification_level = 0
  } = advertiser || defaultAdvertiser;
  
  const isAgent = type === 'agent' || type === 'company';
  const isVerified = verification_level >= 1;
  
  const getVerificationStatus = () => {
    if (verification_level >= 2) return { text: 'Business verified', verified: true };
    if (verification_level >= 1) return { text: 'Identity verified', verified: true };
    return { text: 'Unverified', verified: false };
  };
  
  const verificationStatus = getVerificationStatus();
  const iconSize = size === 'small' ? 'w-6 h-6' : 'w-8 h-8';
  const innerIconSize = size === 'small' ? 'w-3 h-3' : 'w-4 h-4';
  
  return (
    <div 
      className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onAdvertiserClick?.();
      }}
      data-testid="advertiser-identity"
    >
      {/* Avatar */}
      {logo ? (
        <img src={logo} alt={name} className={`${iconSize} rounded-full object-cover border border-gray-200`} />
      ) : (
        <div className={`${iconSize} rounded-full bg-gray-200 flex items-center justify-center`}>
          {isAgent ? (
            <Building2 className={`${innerIconSize} text-gray-500`} />
          ) : (
            <User className={`${innerIconSize} text-gray-500`} />
          )}
        </div>
      )}
      
      {/* Name + Verification */}
      <div className="flex items-center gap-1">
        <span className={`font-medium text-gray-900 ${size === 'small' ? 'text-sm' : 'text-base'}`}>
          {name}
        </span>
        <span className="text-gray-400">·</span>
        {verificationStatus.verified ? (
          <span className={`flex items-center gap-0.5 ${isAgent ? 'text-blue-600' : 'text-green-600'} ${size === 'small' ? 'text-xs' : 'text-sm'}`}>
            <CheckCircle2 className="w-3 h-3" />
            {verificationStatus.text}
          </span>
        ) : (
          <span className={`text-gray-400 italic ${size === 'small' ? 'text-xs' : 'text-sm'}`}>
            {verificationStatus.text}
          </span>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// IMAGE CAROUSEL COMPONENT
// =============================================================================
const ImageCarousel = ({ images, maxImages = 10, listingId, onFavoriteClick, isFavorite }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Limit images based on subscription
  const displayImages = images.slice(0, maxImages);
  const totalImages = displayImages.length;
  
  const goToNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % totalImages);
  };
  
  const goToPrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
  };
  
  const currentImage = displayImages[currentIndex] || 'https://via.placeholder.com/800x450?text=No+Image';
  const imageUrl = currentImage.startsWith('http') ? currentImage : `${API_URL}${currentImage}`;
  
  return (
    <div 
      className="relative w-full h-full overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="image-carousel"
    >
      <img
        src={imageUrl}
        alt={`Property image ${currentIndex + 1}`}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        onError={(e) => { e.target.src = 'https://via.placeholder.com/800x450?text=No+Image'; }}
      />
      
      {/* Navigation arrows - appear on hover */}
      {totalImages > 1 && (
        <>
          <button
            onClick={goToPrev}
            className={`absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-sm bg-white/90 border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-white shadow-sm transition-all ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
            aria-label="Previous image"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToNext}
            className={`absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-sm bg-white/90 border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-white shadow-sm transition-all ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
            aria-label="Next image"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
      
      {/* Image counter - bottom right with camera icon (like Rightmove/Zoopla) */}
      {totalImages > 1 && (
        <div 
          className="absolute bottom-3 right-3 px-2.5 py-1.5 bg-indigo-900/90 rounded-md text-white text-xs font-medium flex items-center gap-1.5"
          data-testid="image-counter"
        >
          <Camera className="w-3.5 h-3.5" />
          <span>{currentIndex + 1}/{totalImages}</span>
        </div>
      )}
      
      {/* Favorite button */}
      <button
        onClick={onFavoriteClick}
        className={`absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white transition-all ${
          isFavorite ? 'text-red-500' : 'text-gray-500'
        }`}
        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        data-testid="favorite-button"
      >
        <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
      </button>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const PropertyResultCard = ({ 
  listing, 
  onHover,
  onFocus,
  showCompare = false,
  isCompareSelected = false,
  onCompareToggle,
  distance,
  variant = 'desktop'
}) => {
  const navigate = useNavigate();
  const { formatCurrency, currency, country } = useCountry();
  const [isFavorite, setIsFavorite] = useState(listing.is_favorite || false);
  
  // Determine distance unit based on country
  const isUK = country === 'UK' || currency === 'GBP';
  const distanceUnit = isUK ? 'mile' : 'km';
  
  // Images - max based on subscription level
  const maxImages = listing.subscription_image_limit || 10;
  const images = listing.images?.length > 0 
    ? listing.images 
    : ['https://via.placeholder.com/800x450?text=No+Image'];

  // Price formatting
  const monthlyPrice = listing.price || 0;
  const weeklyPrice = Math.round(monthlyPrice / 4.33);
  
  const formatPrice = (price) => {
    if (isUK) return `£${price.toLocaleString()}`;
    return formatCurrency(price);
  };

  // Property type - never show just "Property"
  const getPropertyType = () => {
    const type = listing.property_type || listing.type || '';
    const typeNormalized = type.toLowerCase();
    
    const typeMapping = {
      'property': 'Apartment',
      'residential': 'Apartment',
      '': 'Apartment'
    };
    
    return typeMapping[typeNormalized] || 
           (type.charAt(0).toUpperCase() + type.slice(1)) || 
           'Apartment';
  };

  // Bed/bath values
  const bedrooms = listing.bedrooms || listing.beds || 0;
  const bathrooms = listing.bathrooms || listing.baths || 0;

  // Location
  const location = listing.area || listing.location || listing.postcode || 'Location not specified';

  // Availability
  const getAvailability = () => {
    const availableFrom = listing.available_from || listing.availability_date;
    if (!availableFrom) return { text: 'Available now', icon: '🟢' };
    
    const date = new Date(availableFrom);
    const now = new Date();
    
    if (date <= now) return { text: 'Available now', icon: '🟢' };
    
    const formattedDate = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return { text: `Available ${formattedDate}`, icon: '📅' };
  };
  
  const availability = getAvailability();

  // Short description - truncate to ~2 lines
  const getShortDescription = () => {
    const desc = listing.description || listing.short_description || '';
    if (desc.length > 150) {
      return desc.substring(0, 147) + '...';
    }
    return desc;
  };

  // Is listing new (within 7 days)
  const isNew = () => {
    if (!listing.created_at) return false;
    const createdDate = new Date(listing.created_at);
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return createdDate > sevenDaysAgo;
  };

  // Event handlers
  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API_URL}/api/user/favorites/${listing.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
      } else {
        await axios.post(`${API_URL}/api/user/favorites/${listing.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const handleContact = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === 'call' && listing.contact_phone) {
      window.location.href = `tel:${listing.contact_phone}`;
    } else if (type === 'email' && listing.contact_email) {
      window.location.href = `mailto:${listing.contact_email}`;
    } else {
      navigate(`/listing/${listing.id}?contact=${type}`);
    }
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/listing/${listing.id}`;
    if (navigator.share) {
      navigator.share({ title: listing.title, text: `Check out: ${listing.title}`, url });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCompareToggle?.(listing.id);
  };

  const handleAdvertiserClick = () => {
    if (listing.advertiser?.id) {
      navigate(`/advertiser/${listing.advertiser.id}`);
    }
  };

  // ==========================================================================
  // MOBILE LAYOUT
  // ==========================================================================
  if (variant === 'mobile') {
    return (
      <Card className={`overflow-hidden bg-white ${isCompareSelected ? 'ring-2 ring-blue-500' : ''}`}>
        <Link to={`/listing/${listing.id}`}>
          <div className="relative aspect-video">
            <ImageCarousel 
              images={images}
              maxImages={maxImages}
              listingId={listing.id}
              onFavoriteClick={handleFavorite}
              isFavorite={isFavorite}
            />
            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
              {listing.is_demo && (
                <Badge className="bg-gray-200 text-gray-700 border border-gray-300 text-xs font-semibold uppercase tracking-wide" data-testid={`demo-badge-${listing.id}`}>
                  Demo
                </Badge>
              )}
              {listing.featured && (
                <Badge className="bg-amber-500 text-white text-xs">Featured</Badge>
              )}
              {isNew() && (
                <Badge className="bg-emerald-500 text-white text-xs">New</Badge>
              )}
            </div>
          </div>
          
          <div className="p-4 space-y-2">
            <div className="flex justify-between items-start">
              <div className="text-xl font-bold text-gray-900">
                {formatPrice(monthlyPrice)}
                <span className="text-sm font-normal text-gray-500"> pcm</span>
              </div>
              <span className="text-sm text-green-600 font-medium">
                {availability.icon} {availability.text}
              </span>
            </div>
            
            <div className="text-sm text-gray-700">
              {getPropertyType()}
            </div>
            
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {location}
            </div>
            
            {getShortDescription() && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {getShortDescription()}
              </p>
            )}
            
            <AdvertiserIdentity advertiser={listing.advertiser} size="small" />
          </div>
        </Link>
        
        <div className="px-4 pb-4">
          <Button className="w-full" onClick={(e) => handleContact(e, 'message')}>
            Contact
          </Button>
        </div>
      </Card>
    );
  }

  // ==========================================================================
  // DESKTOP LAYOUT (Matching exact design)
  // ==========================================================================
  return (
    <TooltipProvider>
      <Card 
        className={`group overflow-hidden bg-white transition-all duration-200 hover:shadow-lg ${isCompareSelected ? 'ring-2 ring-blue-500' : ''}`}
        onMouseEnter={() => onHover?.(listing.id)}
        onFocus={() => onFocus?.(listing.id)}
        data-testid={`property-card-${listing.id}`}
      >
        <Link to={`/listing/${listing.id}`} className="block">
          <div className="flex flex-row">
            {/* LEFT: Image Carousel */}
            <div className="relative w-[320px] lg:w-[380px] flex-shrink-0">
              <div className="aspect-[4/3] relative overflow-hidden rounded-l-lg">
                <ImageCarousel 
                  images={images}
                  maxImages={maxImages}
                  listingId={listing.id}
                  onFavoriteClick={handleFavorite}
                  isFavorite={isFavorite}
                />
                
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
                  {listing.is_demo && (
                    <Badge className="bg-gray-200 text-gray-700 border border-gray-300 text-xs font-semibold uppercase tracking-wide shadow" data-testid={`demo-badge-${listing.id}`}>
                      Demo
                    </Badge>
                  )}
                  {listing.featured && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge className="bg-amber-500 text-white text-xs font-medium shadow cursor-help">
                          Featured
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>Featured listings are promoted</TooltipContent>
                    </Tooltip>
                  )}
                  {isNew() && (
                    <Badge className="bg-emerald-500 text-white text-xs font-medium shadow">
                      New
                    </Badge>
                  )}
                  {listing.reduced && (
                    <Badge className="bg-red-500 text-white text-xs font-medium shadow">
                      Reduced
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            {/* RIGHT: Content */}
            <div className="flex-1 p-5 flex flex-col min-w-0">
              {/* Row 1: Price + Location */}
              <div className="flex justify-between items-start mb-3">
                {/* Price */}
                <div>
                  <span className="text-2xl font-bold text-blue-600">
                    {formatPrice(monthlyPrice)}
                  </span>
                  <span className="text-base text-gray-500 ml-1">pcm</span>
                  {isUK && weeklyPrice && (
                    <span className="text-base text-gray-400 ml-2 italic">
                      {formatPrice(weeklyPrice)} pw
                    </span>
                  )}
                </div>
                
                {/* Location */}
                <div className="text-right">
                  <div className="flex items-center gap-1 text-gray-700 justify-end">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{location}</span>
                  </div>
                  {/* Availability */}
                  <div className="text-green-600 font-medium mt-1">
                    {availability.icon} {availability.text}
                  </div>
                </div>
              </div>
              
              {/* Row 2: Property Type + Bed/Bath */}
              <div className="flex items-center gap-4 text-gray-700 mb-2">
                <span className="font-medium">{getPropertyType()}</span>
                <span className="flex items-center gap-1 text-gray-500">
                  <BedDouble className="w-4 h-4" />
                  {bedrooms} bed
                </span>
                <span className="text-gray-300">·</span>
                <span className="flex items-center gap-1 text-gray-500">
                  <Bath className="w-4 h-4" />
                  {bathrooms} bath
                </span>
              </div>
              
              {/* Row 3: Distance */}
              {distance && (
                <div className="flex items-center gap-1 text-gray-500 text-sm mb-2" data-testid="property-distance">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{distance} {distanceUnit} away</span>
                </div>
              )}
              
              {/* Row 4: Short Description */}
              {getShortDescription() && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {getShortDescription()}
                </p>
              )}
              
              {/* Row 5: Advertiser */}
              <div className="mb-4">
                <AdvertiserIdentity 
                  advertiser={listing.advertiser}
                  onAdvertiserClick={handleAdvertiserClick}
                />
              </div>
              
              {/* Spacer */}
              <div className="flex-1" />
            </div>
          </div>
          
          {/* Bottom Action Bar */}
          <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between bg-gray-50/50">
            {/* Left: Advertiser (compact) */}
            <AdvertiserIdentity 
              advertiser={listing.advertiser}
              onAdvertiserClick={handleAdvertiserClick}
              size="small"
            />
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Call */}
              <Button 
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={(e) => handleContact(e, 'call')}
              >
                <Phone className="w-4 h-4 mr-1.5" />
                Call
              </Button>
              
              {/* Message */}
              <Button 
                size="sm"
                variant="outline"
                onClick={(e) => handleContact(e, 'message')}
              >
                <MessageCircle className="w-4 h-4 mr-1.5" />
                Message
              </Button>
              
              {/* Email */}
              <Button 
                size="sm"
                variant="outline"
                onClick={(e) => handleContact(e, 'email')}
              >
                <Mail className="w-4 h-4 mr-1.5" />
                Email
              </Button>
              
              {/* Divider */}
              <div className="w-px h-6 bg-gray-200 mx-1" />
              
              {/* Save */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleFavorite}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${isFavorite ? 'text-red-500' : 'text-gray-400'}`}
                  >
                    <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{isFavorite ? 'Remove from saved' : 'Save property'}</TooltipContent>
              </Tooltip>
              
              {/* Share */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleShare}
                    className="p-2 rounded hover:bg-gray-100 text-gray-400 transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Share property</TooltipContent>
              </Tooltip>
              
              {/* Compare */}
              {showCompare && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleCompare}
                      className={`p-2 rounded transition-colors ${
                        isCompareSelected 
                          ? 'bg-blue-600 text-white' 
                          : 'hover:bg-gray-100 text-gray-400'
                      }`}
                    >
                      <Plus className={`w-5 h-5 ${isCompareSelected ? 'rotate-45' : ''} transition-transform`} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{isCompareSelected ? 'Remove from compare' : 'Add to compare'}</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </Link>
      </Card>
    </TooltipProvider>
  );
};

export default PropertyResultCard;
